import moment from "moment";
import { II18nextTypes } from "common/src/types/i18n";
import { isNullOrUndefined } from "../utils/isNullOrUndefined";
import { IDayRateType, IEstimateType, ISettingsType, ITimeRateType, ITypeCarType } from "common/src/types";


interface FareResult {
    totalCost: number;
    grandTotal: number;
    convenience_fees: number;
}

interface FareCalculatorProps {
    carType: ITypeCarType;
    settings: ISettingsType;
    estimate?: Partial<IEstimateType>;

}

interface FareCalculatorResponse {
    error?: boolean;
    fare?: FareResult;
    msg?: keyof II18nextTypes
}

const getAdditionalFee = (arrayDays?: IDayRateType[], arrayTimes?: ITimeRateType[]): number => {
    if (!arrayDays?.length && !arrayTimes?.length) {
        return 0;
    }

    const currentMoment = moment();
    const currentDate = currentMoment.format('YYYY-MM-DD');
    const currentTime = currentMoment.format('HH:mm:ss');

    const dateRate = arrayDays?.find(rate => rate.day === currentDate);
    if (dateRate) {
        return dateRate.additionalFee;
    }

    const timeRate = arrayTimes?.find(rate => {
        const startTime = moment(rate.initialTime, 'HH:mm:ss');
        const endTime = moment(rate.finalTime, 'HH:mm:ss');
        const checkTime = moment(currentTime, 'HH:mm:ss');
        return checkTime.isBetween(startTime, endTime, null, '[)');
    });

    return timeRate ? timeRate.additionalFee : 0;
};

export const FareCalculatorNew = ({ estimate, carType, settings }: FareCalculatorProps): FareCalculatorResponse => {
    if (isNullOrUndefined(estimate) || isNullOrUndefined(estimate.routeDetails)) {
        return {
            error: true,
            msg: "no_details_error"
        };
    }

    const { distance_in_km, time_in_secs } = estimate.routeDetails;

    const distance = distance_in_km;
    const time = time_in_secs;

    const additionalFeeByDateOrTime = getAdditionalFee(carType.days, carType.times);

    let baseCalculated = (parseFloat(String(carType.rate_per_unit_distance)) * parseFloat(String(distance))) +
        (parseFloat(String(carType.rate_per_hour || '0')) * (parseFloat(String(time)) / 3600));

    if (carType.base_fare > 0) {
        baseCalculated += parseFloat(String(carType.base_fare));
    }

    let total = baseCalculated > parseFloat(String(carType.min_fare))
        ? baseCalculated
        : parseFloat(String(carType.min_fare));

    let convenienceFee: number = 0;
    if (carType.convenience_fee_type === 'flat') {
        convenienceFee = parseFloat(String(carType.convenience_fees));
    } else {
        convenienceFee = (total * parseFloat(String(carType.convenience_fees))) / 100;
    }

    let grand = total + convenienceFee;

    let additionalFeeGrandTotalByTime = 0;
    let additionalFeeTotalByTime = 0;

    if (additionalFeeByDateOrTime > 0) {
        additionalFeeGrandTotalByTime = (additionalFeeByDateOrTime / 100) * grand;
        additionalFeeTotalByTime = (additionalFeeByDateOrTime / 100) * total;
    }

    const grandTotalWithAdditionalFee = grand + additionalFeeGrandTotalByTime;
    const totalCostWithAdditionalFee = total + additionalFeeTotalByTime;

    const decimalPlaces = settings.decimal || 2;


    return {
        fare: {
            convenience_fees: parseFloat(convenienceFee.toFixed(decimalPlaces)),
            totalCost: parseFloat(totalCostWithAdditionalFee.toFixed(decimalPlaces)),
            grandTotal: parseFloat(grandTotalWithAdditionalFee.toFixed(decimalPlaces))
        }
    };
};